import {
  Alert,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  HairlineCard,
  IconButton,
  Icons,
  iconSize,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  StatusChip,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ContactedRelationship,
  CreatePreVisitContactDto,
  LetterDelivery,
  LetterKind,
  PhoneDirection,
  PhoneOutcome,
  PreVisitChannel,
  PreVisitContactDetail,
} from '@munifocus/contracts-gov';
import {
  CONTACTED_RELATIONSHIPS,
  LETTER_DELIVERIES,
  LETTER_KINDS,
  PHONE_DIRECTIONS,
  PHONE_OUTCOMES,
} from '@munifocus/contracts-gov';
import {
  useCreatePreVisitContact,
  useDeletePreVisitContact,
  usePreVisitContacts,
  useUpdatePreVisitContact,
} from '../lib/pre-visit-contacts-api';
import { useConfirmAction } from '../lib/useConfirmAction';
import { useSubmitValidation } from '../lib/useSubmitValidation';

/**
 * Stage 2 — the calls and letters by which the officer pursues notice
 * (s. 144(2)(a)) or consent (s. 144(6)) before the first inspection. Every
 * attempt is kept, including the ones that reached nobody: sufficiency of notice
 * is judged on what was attempted, so an unanswered call is evidence.
 *
 * Nothing here starts a statutory clock — that is Step 5.3 service of the Form 4
 * (`ServiceRecords`), which this deliberately does not resemble in behaviour
 * even though it mirrors its shape.
 */

/** The channel-specific detail line shown on a saved entry. */
function contactDetail(c: PreVisitContactDetail, t: (key: string) => string): string[] {
  const party = [
    c.partyName,
    c.partyRelationship === 'other'
      ? c.partyRelationshipOther
      : c.partyRelationship
        ? t(`gov.stage2.contacts.relationship.${c.partyRelationship}`)
        : null,
  ].filter(Boolean) as string[];

  if (c.channel === 'phone') {
    return [
      ...party,
      c.phoneNumber,
      c.direction ? t(`gov.stage2.contacts.direction.${c.direction}`) : null,
      c.outcome ? t(`gov.stage2.contacts.outcome.${c.outcome}`) : null,
    ].filter(Boolean) as string[];
  }
  return [
    ...party,
    c.letterKind ? t(`gov.stage2.contacts.letterKind.${c.letterKind}`) : null,
    c.deliveryMethod ? t(`gov.stage2.contacts.delivery.${c.deliveryMethod}`) : null,
    c.mailingAddress,
    c.trackingNumber,
  ].filter(Boolean) as string[];
}

function ContactRow({
  contact,
  fileId,
  onEdit,
}: {
  contact: PreVisitContactDetail;
  fileId: string;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const remove = useDeletePreVisitContact(fileId);
  const confirm = useConfirmAction();
  const detail = contactDetail(contact, t);

  return (
    <Stack spacing={0.5} sx={{ py: 1 }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2">
          {t(`gov.stage2.contacts.channel.${contact.channel}`)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {contact.occurredOn}
          {contact.occurredAtTime ? ` · ${contact.occurredAtTime}` : ''}
        </Typography>
        <StatusChip
          status={contact.successful ? 'success' : 'warning'}
          label={t(
            contact.successful
              ? 'gov.stage2.contacts.result.made'
              : 'gov.stage2.contacts.result.notMade',
          )}
        />
        <Stack direction="row" sx={{ ml: 'auto', gap: 0.5 }}>
          {/* An attempt is commonly logged before its outcome is known — a call
              saved as "no answer" gets its summary after the owner rings back. */}
          <IconButton
            size="small"
            aria-label={t(
              contact.channel === 'phone'
                ? 'gov.stage2.contacts.editPhone'
                : 'gov.stage2.contacts.editLetter',
            )}
            onClick={onEdit}
          >
            <Icons.Pencil size={iconSize.md} />
          </IconButton>
          <IconButton
            size="small"
            aria-label={t('gov.stage2.contacts.delete')}
            disabled={remove.isPending}
            onClick={async () => {
              if (
                !(await confirm({
                  title: t('gov.stage2.contacts.deleteTitle'),
                  body: t('gov.stage2.contacts.deleteBody', { date: contact.occurredOn }),
                  confirmLabel: t('gov.stage2.contacts.deleteConfirm'),
                  tone: 'danger',
                }))
              ) {
                return;
              }
              remove.mutate(contact.id);
            }}
          >
            <Icons.Trash2 size={iconSize.md} />
          </IconButton>
        </Stack>
      </Stack>
      {detail.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {detail.join(' · ')}
        </Typography>
      )}
      {contact.callSummary && <Typography variant="caption">{contact.callSummary}</Typography>}
      {contact.responseReceived && contact.responseSummary && (
        <Typography variant="caption">{contact.responseSummary}</Typography>
      )}
      {contact.notes && <Typography variant="caption">{contact.notes}</Typography>}
    </Stack>
  );
}

function emptyDto(channel: PreVisitChannel): CreatePreVisitContactDto {
  return channel === 'phone'
    ? { channel, occurredOn: '', direction: 'outbound', outcome: 'no_answer' }
    : { channel, occurredOn: '', letterKind: 'pre_visit', deliveryMethod: 'mail' };
}

/**
 * A saved row as form state. Nulls become `undefined` so an untouched field is
 * omitted from the PATCH rather than sent as an explicit clear.
 */
function dtoFromContact(contact: PreVisitContactDetail): CreatePreVisitContactDto {
  const defined = Object.fromEntries(
    Object.entries(contact).filter(([, v]) => v !== null && v !== undefined),
  );
  const { id, fileId, createdAt, officerId, ...rest } = defined as Record<string, unknown>;
  void id;
  void fileId;
  void createdAt;
  void officerId;
  return rest as unknown as CreatePreVisitContactDto;
}

function ContactForm({
  fileId,
  channel,
  editing,
  onDone,
}: {
  fileId: string;
  channel: PreVisitChannel;
  /** The row being edited, or null when logging a new attempt. */
  editing: PreVisitContactDetail | null;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreatePreVisitContact(fileId);
  const update = useUpdatePreVisitContact(fileId);
  const [dto, setDto] = useState<CreatePreVisitContactDto>(() =>
    editing ? dtoFromContact(editing) : emptyDto(channel),
  );

  const set = (patch: Partial<CreatePreVisitContactDto>) => setDto((p) => ({ ...p, ...patch }));
  const v = useSubmitValidation({
    occurredOn: dto.occurredOn ? null : t('common.validation.required'),
  });

  // Hiding an input must also drop its value, or a field the officer can no
  // longer see still gets submitted. The server normalises too — this keeps the
  // form honest.
  const setDelivery = (deliveryMethod: LetterDelivery) =>
    set({
      deliveryMethod,
      ...(deliveryMethod === 'registered_mail' ? {} : { trackingNumber: undefined }),
    });

  const setRelationship = (partyRelationship: ContactedRelationship | undefined) =>
    set({
      partyRelationship,
      ...(partyRelationship === 'other' ? {} : { partyRelationshipOther: undefined }),
    });

  const saving = create.isPending || update.isPending;

  const submit = () => {
    if (editing) {
      // `channel` is fixed at creation — a row's populated columns belong to the
      // one it was created with, so the update DTO cannot carry it.
      const { channel: _channel, ...patch } = dto;
      update.mutate({ id: editing.id, dto: patch }, { onSuccess: onDone });
      return;
    }
    create.mutate(dto, {
      onSuccess: () => {
        setDto(emptyDto(channel));
        onDone();
      },
    });
  };

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {(create.isError || update.isError) && (
        <Alert severity="error">{t('gov.stage2.contacts.saveError')}</Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          fullWidth
          size="small"
          required
          type="date"
          label={t('gov.stage2.contacts.fields.occurredOn')}
          {...v.field('occurredOn')}
          slotProps={{ inputLabel: { shrink: true } }}
          value={dto.occurredOn}
          onChange={(e) => set({ occurredOn: e.target.value })}
        />
        {/* A letter records a date only; s. 144(2)(a)'s "reasonable hour" is
            about the call arranging the visit. */}
        {channel === 'phone' && (
          <TextField
            fullWidth
            size="small"
            type="time"
            label={t('gov.stage2.contacts.fields.occurredAtTime')}
            slotProps={{ inputLabel: { shrink: true } }}
            value={dto.occurredAtTime ?? ''}
            onChange={(e) => set({ occurredAtTime: e.target.value || undefined })}
          />
        )}
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          fullWidth
          size="small"
          label={t('gov.stage2.contacts.fields.partyName')}
          value={dto.partyName ?? ''}
          onChange={(e) => set({ partyName: e.target.value || undefined })}
        />
        <FormControl fullWidth size="small">
          <InputLabel>{t('gov.stage2.contacts.fields.partyRelationship')}</InputLabel>
          <Select
            label={t('gov.stage2.contacts.fields.partyRelationship')}
            aria-label={t('gov.stage2.contacts.fields.partyRelationship')}
            value={dto.partyRelationship ?? ''}
            onChange={(e) =>
              setRelationship((e.target.value || undefined) as ContactedRelationship | undefined)
            }
          >
            {CONTACTED_RELATIONSHIPS.map((r) => (
              <MenuItem key={r} value={r}>
                {t(`gov.stage2.contacts.relationship.${r}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* An unexplained `other` names nobody, and this row is evidence of who
          the officer reached — so the API requires it too. */}
      {dto.partyRelationship === 'other' && (
        <TextField
          fullWidth
          size="small"
          required
          label={t('gov.stage2.contacts.fields.partyRelationshipOther')}
          value={dto.partyRelationshipOther ?? ''}
          onChange={(e) => set({ partyRelationshipOther: e.target.value || undefined })}
        />
      )}

      {/* ── Phone ─────────────────────────────────────────────────────────── */}
      {channel === 'phone' && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              size="small"
              label={t('gov.stage2.contacts.fields.phoneNumber')}
              value={dto.phoneNumber ?? ''}
              onChange={(e) => set({ phoneNumber: e.target.value || undefined })}
            />
            <FormControl fullWidth size="small">
              <InputLabel>{t('gov.stage2.contacts.fields.direction')}</InputLabel>
              <Select
                label={t('gov.stage2.contacts.fields.direction')}
                aria-label={t('gov.stage2.contacts.fields.direction')}
                value={dto.direction ?? 'outbound'}
                onChange={(e) => set({ direction: e.target.value as PhoneDirection })}
              >
                {PHONE_DIRECTIONS.map((d) => (
                  <MenuItem key={d} value={d}>
                    {t(`gov.stage2.contacts.direction.${d}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <FormControl fullWidth size="small">
            <InputLabel>{t('gov.stage2.contacts.fields.outcome')}</InputLabel>
            <Select
              label={t('gov.stage2.contacts.fields.outcome')}
              aria-label={t('gov.stage2.contacts.fields.outcome')}
              value={dto.outcome ?? 'no_answer'}
              onChange={(e) => set({ outcome: e.target.value as PhoneOutcome })}
            >
              {PHONE_OUTCOMES.map((o) => (
                <MenuItem key={o} value={o}>
                  {t(`gov.stage2.contacts.outcome.${o}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label={t('gov.stage2.contacts.fields.callSummary')}
            helperText={t('gov.stage2.contacts.fields.callSummaryHint')}
            value={dto.callSummary ?? ''}
            onChange={(e) => set({ callSummary: e.target.value || undefined })}
          />
        </>
      )}

      {/* ── Letter ────────────────────────────────────────────────────────── */}
      {channel === 'letter' && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('gov.stage2.contacts.fields.letterKind')}</InputLabel>
              <Select
                label={t('gov.stage2.contacts.fields.letterKind')}
                aria-label={t('gov.stage2.contacts.fields.letterKind')}
                value={dto.letterKind ?? 'pre_visit'}
                onChange={(e) => set({ letterKind: e.target.value as LetterKind })}
              >
                {LETTER_KINDS.map((k) => (
                  <MenuItem key={k} value={k}>
                    {t(`gov.stage2.contacts.letterKind.${k}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>{t('gov.stage2.contacts.fields.deliveryMethod')}</InputLabel>
              <Select
                label={t('gov.stage2.contacts.fields.deliveryMethod')}
                aria-label={t('gov.stage2.contacts.fields.deliveryMethod')}
                value={dto.deliveryMethod ?? 'mail'}
                onChange={(e) => setDelivery(e.target.value as LetterDelivery)}
              >
                {LETTER_DELIVERIES.map((d) => (
                  <MenuItem key={d} value={d}>
                    {t(`gov.stage2.contacts.delivery.${d}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <TextField
            fullWidth
            size="small"
            label={t('gov.stage2.contacts.fields.mailingAddress')}
            value={dto.mailingAddress ?? ''}
            onChange={(e) => set({ mailingAddress: e.target.value || undefined })}
          />
          {/* Tracking only exists for registered mail. */}
          {dto.deliveryMethod === 'registered_mail' && (
            <TextField
              fullWidth
              size="small"
              label={t('gov.stage2.contacts.fields.trackingNumber')}
              value={dto.trackingNumber ?? ''}
              onChange={(e) => set({ trackingNumber: e.target.value || undefined })}
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={dto.responseReceived ?? false}
                onChange={(e) =>
                  set({
                    responseReceived: e.target.checked,
                    ...(e.target.checked ? {} : { responseSummary: undefined }),
                  })
                }
              />
            }
            label={t('gov.stage2.contacts.fields.responseReceived')}
          />
          {dto.responseReceived && (
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label={t('gov.stage2.contacts.fields.responseSummary')}
              value={dto.responseSummary ?? ''}
              onChange={(e) => set({ responseSummary: e.target.value || undefined })}
            />
          )}
        </>
      )}

      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        label={t('gov.stage2.contacts.fields.notes')}
        value={dto.notes ?? ''}
        onChange={(e) => set({ notes: e.target.value || undefined })}
      />

      {/* Unchecked by default, and the server agrees: contact is not presumed. */}
      <FormControlLabel
        control={
          <Checkbox
            checked={dto.successful ?? false}
            onChange={(e) => set({ successful: e.target.checked })}
          />
        }
        label={t('gov.stage2.contacts.fields.successful')}
      />
      <Typography variant="caption" color="text.secondary">
        {t(
          channel === 'phone'
            ? 'gov.stage2.contacts.fields.successfulPhoneHint'
            : 'gov.stage2.contacts.fields.successfulLetterHint',
        )}
      </Typography>

      <Stack direction="row" spacing={1}>
        <Button variant="contained" disabled={saving} onClick={v.guard(submit)}>
          {t('common.actions.save')}
        </Button>
        <Button disabled={saving} onClick={onDone}>
          {t('common.actions.cancel')}
        </Button>
      </Stack>
    </Stack>
  );
}

/** Which form is open: a new attempt on a channel, or an existing row. */
type Editor =
  { mode: 'create'; channel: PreVisitChannel } | { mode: 'edit'; contact: PreVisitContactDetail };

export function PreVisitContactLog({ fileId }: { fileId: string }) {
  const { t } = useTranslation();
  const { data, isError } = usePreVisitContacts(fileId);
  const [editor, setEditor] = useState<Editor | null>(null);

  const contacts = data ?? [];

  return (
    <HairlineCard sx={{ p: 2 }}>
      <Typography variant="overline">{t('gov.stage2.contacts.title')}</Typography>
      <Typography variant="caption" color="text.secondary" component="p">
        {t('gov.stage2.contacts.hint')}
      </Typography>

      {/* A failed *load* is not a failed save — telling the officer their entry
          could not be saved when the list merely failed to fetch would send them
          looking for work they never lost. */}
      {isError && <Alert severity="error">{t('gov.stage2.contacts.loadError')}</Alert>}

      {contacts.length === 0 && !editor && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('gov.stage2.contacts.empty')}
        </Typography>
      )}

      {contacts.length > 0 && (
        <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
          {contacts.map((c) => (
            <ContactRow
              key={c.id}
              contact={c}
              fileId={fileId}
              onEdit={() => setEditor({ mode: 'edit', contact: c })}
            />
          ))}
        </Stack>
      )}

      {editor ? (
        <ContactForm
          // Remounts when the target changes, so the form state is rebuilt from
          // the row being edited rather than kept from the previous one.
          key={editor.mode === 'edit' ? editor.contact.id : editor.channel}
          fileId={fileId}
          channel={editor.mode === 'edit' ? editor.contact.channel : editor.channel}
          editing={editor.mode === 'edit' ? editor.contact : null}
          onDone={() => setEditor(null)}
        />
      ) : (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            size="small"
            startIcon={<Icons.Phone size={iconSize.md} />}
            onClick={() => setEditor({ mode: 'create', channel: 'phone' })}
          >
            {t('gov.stage2.contacts.addPhone')}
          </Button>
          <Button
            size="small"
            startIcon={<Icons.Mail size={iconSize.md} />}
            onClick={() => setEditor({ mode: 'create', channel: 'letter' })}
          >
            {t('gov.stage2.contacts.addLetter')}
          </Button>
        </Stack>
      )}
    </HairlineCard>
  );
}
